/**
 * SEO utility functions for dynamic page optimization
 *
 * 修正記錄：
 * 1. og:url / twitter:url 使用傳入的 canonical URL，不用 window.location.href（含 # 對 SEO 有害）
 * 2. dateModified 統一為固定常數 DATE_MODIFIED（與 useSEO.js 保持一致）
 * 3. canonical URL 只更新 href，不刪除再重建（避免閃爍）
 * 4. 2026-05-01 更新 DATE_MODIFIED
 */

const BASE_URL = 'https://lovedays.homy.hk/';

// ✅ datePublished 保留原始發佈日（不可修改，改掉對 SEO 有害）
// ✅ dateModified 更新為最新修改日期
export const DATE_PUBLISHED = '2023-06-01';
export const DATE_MODIFIED  = '2026-05-01';

export const updateSEO = ({
  title,
  description,
  language,
  additionalData = {},
  activeTab,
  calculatorData,
  canonicalUrl = BASE_URL,
  robots = 'index,follow'
}) => {
  document.title = title;
  updateMetaTag('name', 'title', title);
  updateMetaTag('name', 'description', description);
  const keywordsContent = additionalData.keywords
    || generateKeywords(language, activeTab, calculatorData);
  updateMetaTag('name', 'keywords', keywordsContent);
  updateMetaTag('name', 'robots', robots);
  updateSocialMetaTags({ title, description, canonicalUrl });
  updateStructuredData({ title, description, language, additionalData, activeTab, calculatorData });
};

const getCanonicalUrl = (activeTab, calculatorData = {}) => {
  void activeTab;
  void calculatorData;
  return BASE_URL;
};

const generateKeywords = (language, activeTab, calculatorData = {}) => {
  const commonKeywords = language === 'zh'
    ? '日期計算器,紀念日,特殊日子,時間計算,計算工具'
    : 'date calculator,anniversary,special days,time calculator,calculator tool';

  const baseLoveKeywords = language === 'zh'
    ? '戀愛紀念日,愛情計算器,情侶紀念日,愛情里程碑,愛情成就,相識紀念日,結婚紀念日,在一起幾天,戀愛日曆,紀念日計算器'
    : 'love anniversary,relationship calculator,couple days,love milestones,love achievements,dating anniversary,wedding anniversary,days together,love calendar,anniversary calculator';

  const baseDateKeywords = language === 'zh'
    ? '日期計算器,日期差距,工作日計算,日期間隔,倒數日,日期轉換,假期計算,時間計算器,日期相差,日期相距,計算兩個日期之間的天數'
    : 'date calculator,date difference,working days calculator,date span,countdown days,date converter,holiday calculator,time calculator,days between dates,calculate days between two dates';

  const milestoneKeywords = language === 'zh'
    ? '100天紀念,一週年紀念,520天紀念,1314天紀念,百日紀念,週年紀念,特殊紀念日,紀念日計算'
    : '100 days anniversary,one year anniversary,special anniversary days,anniversary calculator';

  let specificKeywords = '';

  if (activeTab === 'love' && calculatorData.anniversaryType) {
    const typeKeywords = {
      'couple': language === 'zh'
        ? '戀愛紀念日,情侶紀念日,交往紀念日,戀愛計算器,情侶計算器,戀人紀念日,戀愛時間計算,在一起幾天,情侶紀念日計算器'
        : 'dating anniversary,relationship days,couple anniversary,dating calculator,relationship counter,love day calculator,days together counter',
      'wedding': language === 'zh'
        ? '結婚紀念日,婚姻紀念日,結婚週年,結婚幾天,結婚紀念日計算器,婚姻時間計算,結婚時間'
        : 'wedding anniversary,marriage anniversary,wedding celebration,wedding day counter,marriage time calculator,wedding day calculator',
      'first-meet': language === 'zh'
        ? '相識紀念日,初次見面,緣分開始,相識計算器,相識時間計算,第一次見面紀念,相遇紀念日'
        : 'first meeting anniversary,first encounter,relationship beginning,first met calculator,encounter anniversary',
      'first-date': language === 'zh'
        ? '首次約會紀念,第一次約會,初次約會,約會紀念日,第一次約會紀念日,約會紀念計算器'
        : 'first date anniversary,first date celebration,dating anniversary calculator,first date counter',
      'proposal': language === 'zh'
        ? '求婚紀念日,求婚日,訂婚紀念,求婚計算器,求婚時間計算,求婚週年,求婚紀念日計算器'
        : 'proposal anniversary,engagement anniversary,proposal day counter,engagement day calculator'
    };

    specificKeywords = typeKeywords[calculatorData.anniversaryType] || '';

    if (calculatorData.calculatedDays) {
      const days = calculatorData.calculatedDays;
      if (days >= 100 && days < 200) {
        specificKeywords += language === 'zh' ? ',100天紀念日,百日紀念,百日' : ',100 days anniversary,hundred days';
      } else if (days >= 365 && days < 730) {
        specificKeywords += language === 'zh' ? ',一週年紀念,一年紀念日,一週年' : ',one year anniversary,first anniversary';
      } else if (days >= 520 && days < 600) {
        specificKeywords += language === 'zh' ? ',520天紀念日,我愛你,520紀念日' : ',520 days anniversary,i love you day';
      } else if (days >= 1314 && days < 1400) {
        specificKeywords += language === 'zh' ? ',1314天紀念日,一生一世,1314紀念' : ',1314 days anniversary,forever anniversary';
      }
    }

    return `${specificKeywords},${baseLoveKeywords},${milestoneKeywords},${commonKeywords}`;
  } else if (activeTab === 'standard') {
    if (calculatorData.calculationMode === 'workdays') {
      const workdayKeywords = language === 'zh'
        ? '工作日計算器,工作日數量,工作天數計算,不含週末,工作日期計算,上班日計算,工作日差距,營業日計算,工作日數,公眾假期不自動扣除,計算工作天數,排除週末'
        : 'working days calculator,business days counter,weekday calculator,exclude weekends,workday counter,business day calculation,work day count,public holidays not automatically excluded,working day calculation,business days between dates';
      return `${workdayKeywords},${baseDateKeywords},${commonKeywords}`;
    }
    return `${baseDateKeywords},${commonKeywords}`;
  }

  return activeTab === 'love'
    ? `${baseLoveKeywords},${milestoneKeywords},${commonKeywords}`
    : `${baseDateKeywords},${commonKeywords}`;
};

const updateSocialMetaTags = ({ title, description, canonicalUrl }) => {
  updateMetaTag('property', 'og:title', title);
  updateMetaTag('property', 'og:description', description);
  updateMetaTag('property', 'og:url', canonicalUrl);
  updateMetaTag('name', 'twitter:title', title);
  updateMetaTag('name', 'twitter:description', description);
  updateMetaTag('name', 'twitter:url', canonicalUrl);
};

const updateMetaTag = (attributeName, attributeValue, content) => {
  const selector = `meta[${attributeName}="${attributeValue}"]`;
  const tags = document.querySelectorAll(selector);
  const tag = tags[0];
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    const newTag = document.createElement('meta');
    newTag.setAttribute(attributeName, attributeValue);
    newTag.setAttribute('content', content);
    document.head.appendChild(newTag);
  }
  Array.from(tags).slice(1).forEach(duplicate => duplicate.remove());
};

const updateStructuredData = ({ title, description, language, additionalData, activeTab, calculatorData }) => {
  const shouldInjectDynamicSchema = false;

  if (!shouldInjectDynamicSchema) {
    document.querySelectorAll('script[type="application/ld+json"][data-dynamic]').forEach(script => script.remove());
    return;
  }

  let structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    'name': title,
    'description': description,
    'url': getCanonicalUrl(activeTab, calculatorData),
    'applicationCategory': activeTab === 'love' ? 'LifestyleApplication' : 'UtilityApplication',
    'operatingSystem': 'Any',
    'author': {
      '@type': 'Organization',
      'name': 'Homy Tech Limited',
      'url': 'https://homy.hk',
      'logo': { '@type': 'ImageObject', 'url': 'https://homy.hk/logo.png' }
    },
    'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' },
    'datePublished': DATE_PUBLISHED,
    'dateModified':  DATE_MODIFIED,
    ...additionalData
  };

  if (activeTab === 'love' && calculatorData?.anniversaryDate) {
    const relationshipType = calculatorData.anniversaryType || 'couple';
    structuredData.mainEntity = {
      '@type': 'Event',
      'name': language === 'zh' ? '愛情紀念日' : 'Love Anniversary',
      'startDate': calculatorData.anniversaryDate.toISOString().split('T')[0],
      'description': `${calculatorData.calculatedDays} ${language === 'zh' ? '天' : 'days'} ${calculatorData.relationshipLabel || ''}`,
      'eventStatus': 'https://schema.org/EventScheduled'
    };

    const relationshipFAQs = {
      couple: {
        zh: { q: '如何慶祝戀愛紀念日？', a: '戀愛紀念日可以透過準備驚喜禮物、共進浪漫晚餐、重溫第一次約會的地點、一起製作紀念相冊或計劃一次特別旅行來慶祝。最重要的是表達對彼此的愛和感激。' },
        en: { q: 'How to celebrate a dating anniversary?', a: 'Celebrate a dating anniversary by preparing surprise gifts, enjoying a romantic dinner, revisiting the location of your first date, creating a memory album together, or planning a special trip.' }
      },
      wedding: {
        zh: { q: '傳統的結婚紀念日禮物有哪些？', a: '傳統結婚紀念日禮物通常根據年數而定，如一週年是紙製品，五週年是木製品，二十五週年是銀製品，五十週年是金製品等。' },
        en: { q: 'What are traditional wedding anniversary gifts?', a: 'Traditional wedding anniversary gifts vary by year: 1st is paper, 5th is wood, 25th is silver, and 50th is gold.' }
      },
      'first-meet': {
        zh: { q: '為什麼相識紀念日很重要？', a: '相識紀念日標誌著你們緣分的開始，慶祝這一天可以幫助你們回顧初次相遇的美好瞬間，感恩命運的安排。' },
        en: { q: 'Why is a first meeting anniversary important?', a: 'The first meeting anniversary marks the beginning of your connection. Celebrating this day helps you reflect on the beautiful moment you first met.' }
      }
    };

    const faqData = relationshipFAQs[relationshipType] || relationshipFAQs.couple;
    const faq = faqData[language] || faqData['zh'];
    structuredData.mainEntityOfPage = {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': language === 'zh' ? '如何計算戀愛紀念日？' : 'How to calculate relationship anniversaries?',
          'acceptedAnswer': { '@type': 'Answer', 'text': language === 'zh' ? '選擇你們的紀念日日期，然後選擇紀念類型，系統會自動計算你們在一起的天數、月數和年數。' : "Select your anniversary date, choose the relationship type, and the system will automatically calculate the days, months, and years you've been together." }
        },
        { '@type': 'Question', 'name': faq.q, 'acceptedAnswer': { '@type': 'Answer', 'text': faq.a } }
      ]
    };
  }

  if (activeTab === 'standard') {
    structuredData.potentialAction = {
      '@type': 'UseAction',
      'target': { '@type': 'EntryPoint', 'urlTemplate': `${BASE_URL}/date-calculator` }
    };
  }

  let scriptTag = document.querySelector('script[type="application/ld+json"][data-dynamic]');
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.dataset.dynamic = 'true';
    document.head.appendChild(scriptTag);
  }
  scriptTag.textContent = JSON.stringify(structuredData);
};

export const setCanonicalLink = (url) => {
  const links = document.querySelectorAll('link[rel="canonical"]');
  let link = links[0];

  if (link) {
    link.href = url;
  } else {
    link = document.createElement('link');
    link.rel = 'canonical';
    link.href = url;
    document.head.appendChild(link);
  }

  Array.from(links).slice(1).forEach(duplicate => duplicate.remove());
};

export const setLanguageAlternates = (urls) => {
  const shouldPublishLanguageAlternates = false;

  if (!shouldPublishLanguageAlternates) {
    void urls;
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    return;
  }

  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

  const fullUrls = {
    'zh-TW':    urls['zh-TW'] || urls['zh'] || `${BASE_URL}?lang=zh`,
    'zh-HK':    urls['zh-TW'] || urls['zh'] || `${BASE_URL}?lang=zh`,
    'zh-CN':    urls['zh-TW'] || urls['zh'] || `${BASE_URL}?lang=zh`,
    'en':       urls['en']    || `${BASE_URL}?lang=en`,
    'en-US':    urls['en']    || `${BASE_URL}?lang=en`,
    'en-GB':    urls['en']    || `${BASE_URL}?lang=en`,
    'x-default':urls['en']    || `${BASE_URL}?lang=en`
  };

  Object.entries(fullUrls).forEach(([lang, url]) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = lang;
    link.href = url;
    document.head.appendChild(link);
  });
};

export const addBreadcrumbStructuredData = ({ language, activeTab, relationshipType = null }) => {
  const shouldInjectDynamicBreadcrumbSchema = false;

  if (!shouldInjectDynamicBreadcrumbSchema) {
    document.querySelectorAll('script[data-type="breadcrumb-schema"]').forEach(script => script.remove());
    return;
  }

  const breadcrumbItems = [
    { '@type': 'ListItem', 'position': 1, 'name': 'Homy', 'item': 'https://homy.hk' },
    { '@type': 'ListItem', 'position': 2, 'name': language === 'zh' ? '愛情時光' : 'Love Days', 'item': BASE_URL }
  ];

  if (activeTab === 'standard') {
    breadcrumbItems.push({
      '@type': 'ListItem',
      'position': 3,
      'name': language === 'zh' ? '日期計算器' : 'Date Calculator',
      'item': `${BASE_URL}/date-calculator`
    });
  } else if (relationshipType) {
    const map = {
      'couple':      { url: 'dating-anniversary',     name: language === 'zh' ? '戀愛紀念日' : 'Dating Anniversary' },
      'wedding':     { url: 'wedding-anniversary',    name: language === 'zh' ? '結婚紀念日' : 'Wedding Anniversary' },
      'first-meet':  { url: 'first-met-anniversary',  name: language === 'zh' ? '相識紀念日' : 'First Meeting Anniversary' },
      'first-date':  { url: 'first-date',             name: language === 'zh' ? '首次約會紀念' : 'First Date Anniversary' },
      'proposal':    { url: 'proposal-anniversary',   name: language === 'zh' ? '求婚紀念日' : 'Proposal Anniversary' }
    };
    if (map[relationshipType]) {
      breadcrumbItems.push({
        '@type': 'ListItem',
        'position': 3,
        'name': map[relationshipType].name,
        'item': `${BASE_URL}/${map[relationshipType].url}`
      });
    }
  }

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbItems
  };

  let script = document.querySelector('script[data-type="breadcrumb-schema"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.type = 'breadcrumb-schema';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(breadcrumbData);
};

export const addDateCalculatorHowToSchema = ({ language, calculationMode = 'all' }) => {
  const shouldInjectDynamicHowToSchema = false;

  if (!shouldInjectDynamicHowToSchema) {
    document.querySelectorAll('script[data-type="howto-schema"]').forEach(script => script.remove());
    return;
  }

  const isWorkdays = calculationMode === 'workdays';
  const howToData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': language === 'zh'
      ? (isWorkdays ? '如何計算兩個日期之間的工作日' : '如何計算兩個日期之間的天數')
      : (isWorkdays ? 'How to Calculate Working Days Between Two Dates' : 'How to Calculate Days Between Two Dates'),
    'description': language === 'zh'
      ? (isWorkdays ? '了解如何計算兩個日期之間的工作日數量，不包括週末。' : '學習如何計算任意兩個日期之間的確切天數和時間差。')
      : (isWorkdays ? 'Learn how to calculate the number of working days between two dates, excluding weekends.' : 'Learn how to calculate the exact number of days and time difference between any two dates.'),
    'tool': { '@type': 'HowToTool', 'name': language === 'zh' ? '日期計算器' : 'Date Calculator' },
    'totalTime': 'PT2M',
    'estimatedCost': { '@type': 'MonetaryAmount', 'currency': 'USD', 'value': '0' },
    'step': [
      { '@type': 'HowToStep', 'name': language === 'zh' ? '選擇開始日期' : 'Select Start Date', 'text': language === 'zh' ? '在日期選擇器中選擇您的開始日期。' : 'Choose your start date using the date selector.', 'url': `${BASE_URL}/date-calculator#start-date` },
      { '@type': 'HowToStep', 'name': language === 'zh' ? '選擇結束日期' : 'Select End Date', 'text': language === 'zh' ? '在第二個日期選擇器中選擇您的結束日期。' : 'Choose your end date using the second date selector.', 'url': `${BASE_URL}/date-calculator#end-date` },
      { '@type': 'HowToStep', 'name': language === 'zh' ? '選擇計算模式' : 'Choose Calculation Mode', 'text': language === 'zh' ? (isWorkdays ? '選擇「工作日」模式來計算工作日（週一至週五，不包括週末）。' : '選擇「所有日期」模式來計算所有日曆日。') : (isWorkdays ? "Select 'Workdays Only' mode to calculate working days (Monday to Friday, excluding weekends)." : "Select 'All Days' mode to calculate all calendar days."), 'url': `${BASE_URL}/date-calculator#calculation-mode` },
      { '@type': 'HowToStep', 'name': language === 'zh' ? '查看結果' : 'View Results', 'text': language === 'zh' ? '系統將自動計算並顯示兩個日期之間的總天數、年數、月數和天數。' : 'The system will automatically calculate and display the total days, years, months, and days between the two dates.', 'url': `${BASE_URL}/date-calculator#results` }
    ]
  };

  let script = document.querySelector('script[data-type="howto-schema"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.type = 'howto-schema';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(howToData);
};

export const addWebsiteStructuredData = (language) => {
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'url': 'https://homy.hk/',
    'name': language === 'zh' ? 'Homy - 實用計算工具集' : 'Homy - Useful Calculation Tools',
    'description': language === 'zh'
      ? '提供各種實用計算工具，包括日期計算器、愛情紀念日計算器等。'
      : 'Providing various useful calculation tools, including date calculators and love anniversary calculators.',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': { '@type': 'EntryPoint', 'urlTemplate': 'https://homy.hk/search?q={search_term_string}' },
      'query-input': 'required name=search_term_string'
    }
  };

  let script = document.querySelector('script[data-type="website-schema"]');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.type = 'website-schema';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(websiteData);
};